import { QueryClientProvider } from "@tanstack/react-query";
import { MotionConfig } from "motion/react";
import { lazy, Suspense, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ApiProvider } from "./api/context";
import { resolveDataMode } from "./api/mode";
import { AuthProvider, RequireSuperadmin } from "./auth/AuthContext";
import { AdminShell } from "./layout/AdminShell";
import { createServices } from "./services";
import { DialogsProvider } from "./ui/components/Dialogs";
import { CenteredLoading } from "./ui/components/States";
import { ToastProvider } from "./ui/components/Toast";

const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const TenantsPage = lazy(() => import("./pages/tenants/TenantsPage"));
const PlansPage = lazy(() => import("./pages/plans/PlansPage"));
const PlatformAuditPage = lazy(() => import("./pages/audit/PlatformAuditPage"));

const { mode, reset } = resolveDataMode();
if (reset) {
  const u = new URL(window.location.href);
  u.searchParams.delete("mock");
  window.history.replaceState(null, "", u.toString());
}
const services = createServices({ mode, reset });

export function Providers({ services: s, children }: { services: ReturnType<typeof createServices>; children: ReactNode }) {
  return (
    <QueryClientProvider client={s.queryClient}>
      <ApiProvider value={s.api}>
        <AuthProvider>
          <MotionConfig reducedMotion="user">
            <DialogsProvider>
              <ToastProvider>{children}</ToastProvider>
            </DialogsProvider>
          </MotionConfig>
        </AuthProvider>
      </ApiProvider>
    </QueryClientProvider>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Providers services={services}>
        <Suspense fallback={<CenteredLoading />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              element={
                <RequireSuperadmin>
                  <AdminShell />
                </RequireSuperadmin>
              }
            >
              <Route path="/admin/tenants" element={<TenantsPage />} />
              <Route path="/admin/plans" element={<PlansPage />} />
              <Route path="/admin/audit-log" element={<PlatformAuditPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/admin/tenants" replace />} />
          </Routes>
        </Suspense>
      </Providers>
    </BrowserRouter>
  );
}
